import {
  NOT_AVAILABLE_LIENHOLDERS_PER_STATE,
  OtherLienholderSlug,
  isTreatedAsOther,
} from '../../../../constants/lienholders';
import {
  UNABLE_TO_ASSIST_ERROR_MSG,
  UNAVAILABLE_LIEHOLDER_ERROR_MSG,
} from '../../../../constants/tempInfos';
import { Maybe, StateAbbreviation } from '../../../../gql/generated/graphql';
import { Lienholder } from '../../../../gql/prs/types';

export const lienholderNotAvailableInState = (
  state: Maybe<string> | null,
  lienholderSlug: Maybe<string>,
) => {
  if (!state || !lienholderSlug) {
    return false;
  }

  return NOT_AVAILABLE_LIENHOLDERS_PER_STATE[state as StateAbbreviation]?.includes(lienholderSlug);
};

// Lowercase, strip non-alphanumerics, and collapse repeated characters
// ("allllly" -> "aly") so misspellings and spacing/punctuation don't block a match.
const normalizeForSearch = (value: string): string =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')
    .replace(/(.)\1+/g, '$1');

// Levenshtein distance
const editDistance = (a: string, b: string): number => {
  if (!a.length) return b.length;
  if (!b.length) return a.length;
  let prevRow = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i += 1) {
    const currRow = [i];
    for (let j = 1; j <= b.length; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      currRow[j] = Math.min(prevRow[j] + 1, currRow[j - 1] + 1, prevRow[j - 1] + cost);
    }
    prevRow = currRow;
  }
  return prevRow[b.length];
};

// Typo-tolerant matcher for the lienholder search: matches the query against
// the display name + aliases by prefix, substring, or small edit distance.
// Plugged into the Select's react-select `filterOption`.
export const matchesLienholderSearch = (fields: string[], query: string): boolean => {
  const normalizedQuery = normalizeForSearch(query);
  if (!normalizedQuery) {
    return true;
  }

  const fuzzyThreshold = normalizedQuery.length <= 4 ? 1 : 2;

  return fields
    .map(normalizeForSearch)
    .filter(Boolean)
    .some(
      (field) =>
        field.includes(normalizedQuery) ||
        (normalizedQuery.length >= 3 && editDistance(field, normalizedQuery) <= fuzzyThreshold),
    );
};

const AUTO_MATCH_NOISE_WORDS = new Set(['the', 'bank', 'financial', 'finance', 'services']);

// Same normalization as search, but also drops noise words ("Ally Bank" -> "ally")
// so generic suffixes don't block an exact hit. Falls back to keeping the noise
// words when dropping them would leave nothing (e.g. "Fifth Third Bank" vs "Bank").
const normalizeForAutoMatch = (value: string): string => {
  const words = value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  const meaningful = words.filter((word) => !AUTO_MATCH_NOISE_WORDS.has(word));

  return (meaningful.length ? meaningful : words).join('').replace(/(.)\1+/g, '$1');
};

export type LienholderAutoMatchResult =
  | { match: Lienholder; method: 'exact' | 'fuzzy' }
  | { match: null; reason: 'empty_query' | 'no_match' | 'ambiguous'; candidates: string[] };

// Middle-of-the-road matching per LE-17491: exact normalized hits always map;
// fuzzy maps only on a single clear winner; ambiguity always abstains (stays Other).
export const autoMatchLienholder = (
  lienholders: Lienholder[],
  typedValue: string,
): LienholderAutoMatchResult => {
  const normalizedQuery = normalizeForAutoMatch(typedValue);
  if (!normalizedQuery) {
    return { match: null, reason: 'empty_query', candidates: [] };
  }

  const searchable = lienholders.filter((lienholder) => lienholder.slug !== OtherLienholderSlug);
  const normalizedFields = (lienholder: Lienholder) =>
    [lienholder.display_name, ...(lienholder.aliases ?? [])].map(normalizeForAutoMatch);

  const exactMatches = searchable.filter((lienholder) =>
    normalizedFields(lienholder).some((field) => field === normalizedQuery),
  );
  if (exactMatches.length === 1) {
    return { match: exactMatches[0], method: 'exact' };
  }
  if (exactMatches.length > 1) {
    return {
      match: null,
      reason: 'ambiguous',
      candidates: exactMatches.map((lienholder) => lienholder.slug),
    };
  }

  // Short entries don't carry enough signal for a fuzzy guess.
  if (normalizedQuery.length < 3) {
    return { match: null, reason: 'no_match', candidates: [] };
  }

  const fuzzyThreshold = normalizedQuery.length <= 4 ? 1 : 2;
  const closeMatches = searchable.filter((lienholder) =>
    normalizedFields(lienholder).some(
      (field) => !!field && editDistance(field, normalizedQuery) <= fuzzyThreshold,
    ),
  );
  if (closeMatches.length === 1) {
    return { match: closeMatches[0], method: 'fuzzy' };
  }

  return {
    match: null,
    reason: closeMatches.length ? 'ambiguous' : 'no_match',
    candidates: closeMatches.map((lienholder) => lienholder.slug),
  };
};

// Structurally compatible with the Select component's Option.
export type LienholderSearchOption = { label: string; value: string; aliases: string[] };

// Search options for the modal — no "Other" entry; manual entry is a separate
// last-resort path.
export const toLienholderSearchOptions = (lienholders: Lienholder[]): LienholderSearchOption[] =>
  lienholders
    .filter((lienholder) => lienholder.slug !== OtherLienholderSlug)
    .map((lienholder) => ({
      label: lienholder.display_name,
      value: lienholder.display_name,
      aliases: lienholder.aliases ?? [],
    }))
    .sort((leftOption, rightOption) => leftOption.label.localeCompare(rightOption.label));

// react-select `filterOption` for the lienholder search Select.
export const filterLienholderSearchOption = (
  option: { label: string; value: string; data: LienholderSearchOption },
  inputValue: string,
): boolean => matchesLienholderSearch([option.label, ...option.data.aliases], inputValue);

export type LienholderSelectionError = {
  message: string;
  reason: 'not_available_in_state' | 'lienholder_disabled';
};

// Support rules for selecting a lienholder — shared between picking from the
// search list and a confident auto-match, which must behave exactly alike.
export const getLienholderSelectionError = (
  lienholder: Lienholder,
  licensePlateState: Maybe<string> | null,
  skipLienholderCheck: boolean,
): LienholderSelectionError | null => {
  if (skipLienholderCheck) {
    return null;
  }

  if (lienholderNotAvailableInState(licensePlateState || null, lienholder.slug)) {
    return { message: UNABLE_TO_ASSIST_ERROR_MSG, reason: 'not_available_in_state' };
  }

  const treatedAsOther = isTreatedAsOther({
    lienholder: lienholder.display_name,
    lienholder_slug: lienholder.slug,
    is_core: lienholder.is_core,
  });

  if (!treatedAsOther && !lienholder.enabled) {
    return { message: UNAVAILABLE_LIEHOLDER_ERROR_MSG, reason: 'lienholder_disabled' };
  }

  return null;
};

export type ManualLienholderResolution =
  | { action: 'map'; lienholder: Lienholder; method: 'exact' | 'fuzzy'; treatedAsOther: boolean }
  | {
      action: 'block';
      lienholder: Lienholder;
      message: string;
      reason: LienholderSelectionError['reason'];
    }
  | { action: 'abstain'; reason: 'empty_query' | 'no_match' | 'ambiguous'; candidates: string[] };

// Decision for a manual free-text entry: map a confident match exactly
// as if it had been picked from the list.
export const resolveManualLienholderEntry = (
  lienholders: Lienholder[],
  typedValue: string,
  licensePlateState: Maybe<string> | null,
  skipLienholderCheck: boolean,
): ManualLienholderResolution => {
  const result = autoMatchLienholder(lienholders, typedValue);
  if (!result.match) {
    return { action: 'abstain', reason: result.reason, candidates: result.candidates };
  }

  const matched = result.match;
  const selectionError = getLienholderSelectionError(
    matched,
    licensePlateState,
    skipLienholderCheck,
  );
  if (selectionError) {
    return { action: 'block', lienholder: matched, ...selectionError };
  }

  return {
    action: 'map',
    lienholder: matched,
    method: result.method,
    treatedAsOther: isTreatedAsOther({
      lienholder: matched.display_name,
      lienholder_slug: matched.slug,
      is_core: matched.is_core,
    }),
  };
};
